import { __decorate } from "tslib";
import { AutoEncoder, BooleanDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { v4 as uuidv4 } from 'uuid';
import { BalanceItemType, VATExcemptReason } from '../BalanceItem.js';
import { SimpleError } from '@simonbackx/simple-errors';
import { STMath } from '@stamhoofd/utility';
export class InvoicedBalanceItem extends AutoEncoder {
    id;
    balanceItemId = '';
    type = BalanceItemType.Other;
    name = '';
    description = '';
    /**
     * Price of the balance that was actually invoiced. Always includes VAT.
     *
     * This only explains the due balance that was invoiced so we don't invoice items multiple times. It does not correspond to the visible unit price or price on the invoice.
     *
     * - E.g. if balance item price is 5 euro including VAT (=balanceInvoicedAmount), then unit price would be 4,13 euro. You'll see this causes a rounding issue if the amount is 2, because then the price including VAT of the invoice would be 9,99 instead of 10.
     * - E.g. if balance item price is 0,242 euro including VAT (=balanceInvoicedAmount), then unit price would be 0,20 euro (no rounding issue)
     * - E.g. if balance item price is 0,243 euro including VAT (=balanceInvoicedAmount), then unit price would also be 0,20 euro (rounding issue)
     *
     * Any rounding issues are fixed on the invoice level.
     */
    balanceInvoicedAmount = 0;
    /**
     * quantity per ten thousand
     * We need to support 4 decimals in case of partial payments.
     * E.g. 10 euro of 30 euro was paid and is invoiced, then the amount would be 0.3333, stored as 3333
     * Mostly for legal reasons, to be able to say wat was actually bought without faking an amount
     */
    quantity = 1_00_00;
    /**
     * Price per piece
     * Always excludes VAT
     */
    unitPrice = 0;
    /**
     * This is duplicated to allow altering the vat percentage of the corresponding balance item later, so
     * mistakes can be corrected and credited.
     */
    VATPercentage = 0;
    /**
     * Note: does not apply to unitPrice, only to the balance item at the time of invoicing
     */
    VATIncluded = false;
    /**
     * This is duplicated to allow altering the vat percentage of the corresponding balance item later, so
     * mistakes can be corrected and credited.
     */
    VATExcempt = null;
    /**
     * This should be rounded to 2 decimals to match PEPPOL requirements
     * = LineExtensionAmount
     */
    get totalWithoutVAT() {
        return STMath.round(this.unitPrice * this.quantity / 1_00_00_00) * 100;
    }
    /**
     * Total without VAT, but not rounded
     */
    get preciseTotalWithoutVAT() {
        if (this.VATExcempt) {
            return this.balanceInvoicedAmount;
        }
        return this.balanceInvoicedAmount * 100 / (100 + this.VATPercentage);
    }
    /**
     * Debug helper
     */
    addedToUnitPriceToCorrectVAT = 0;
    /**
     * Creates a default configuration for a balance item that needs to be invoiced. The result can still be adjusted to correct rounding issues, because
     * a balanace item cannot always be perfectly represented on an invoice (on an invoice rounding happens on invoice level, while balance items can have any price that is not really rounded at any time unless when paid).
     *
     * @param balanceItem
     * @param amount paid (or to be paid) price, should always include VAT if there is VAT
     */
    static createFor(balanceItem, amount) {
        if (balanceItem.VATPercentage === null) {
            throw new SimpleError({
                message: 'Cannot create InvoicedBalanceItem for balance item without VAT percentage',
                human: $t('%1TA', { item: balanceItem.itemTitle }),
                code: 'balance_item_without_vat_percentage',
            });
        }
        const item = new InvoicedBalanceItem();
        item.type = balanceItem.type;
        item.balanceItemId = balanceItem.id;
        item.name = balanceItem.itemTitle;
        item.description = balanceItem.itemDescription ?? '';
        item.balanceInvoicedAmount = amount;
        item.VATPercentage = balanceItem.VATPercentage;
        item.VATExcempt = balanceItem.VATExcempt;
        item.VATIncluded = balanceItem.VATIncluded;
        // Calculate amount and unit price
        const balanceItemUnitPriceWithVAT = STMath.round(balanceItem.priceWithVAT / balanceItem.quantity);
        const quantity = balanceItemUnitPriceWithVAT === 0 ? 1_00_00 : STMath.round(item.balanceInvoicedAmount * 1_00_00 / balanceItemUnitPriceWithVAT); // Amount per ten thousand
        if (quantity === 0 || isNaN(quantity) || quantity === null || quantity === undefined) {
            throw new SimpleError({
                message: 'Invoiced amount is too low to create an invoiced balance item',
                human: $t('%1Ik'),
                code: 'invoiced_amount_too_low',
            });
        }
        item.quantity = quantity;
        item.unitPrice = amount * 1_00_00 / quantity;
        // Remove VAT from unit price if needed
        if (!balanceItem.VATExcempt) {
            item.unitPrice = item.unitPrice * 100 / (100 + balanceItem.VATPercentage);
        }
        // Rounding the unit price is not a peppol requirement normally,
        // so this is disabled for now. Rounding will happen on line level though
        // item.unitPrice = Math.round(item.unitPrice / 100) * 100;
        // round as integer instead (0.0001):
        item.unitPrice = STMath.round(item.unitPrice);
        // Make sure unit price is always positive, and quantity is negative instead
        if (item.unitPrice < 0) {
            item.unitPrice = -item.unitPrice;
            item.quantity = -item.quantity;
        }
        item.roundUnitPriceWithoutDifferenceInTotal();
        return item;
    }
    roundUnitPriceWithoutDifferenceInTotal() {
        // Round unit price cleaner
        const recalculated = STMath.round(this.totalWithoutVAT * 10_000 / this.quantity / 20) * 20; // 15_00_00
        if (recalculated !== this.unitPrice && (recalculated % 100 === 0 || recalculated % 100 === 5)) {
            // Only allow clean rounding like 15,455 or 15,45.
            if (STMath.round(recalculated * this.quantity / 1_00_00_00) * 100 === this.totalWithoutVAT) {
                if (this.addedToUnitPriceToCorrectVAT !== 0) {
                    const originalUnitPrice = this.unitPrice - this.addedToUnitPriceToCorrectVAT;
                    this.addedToUnitPriceToCorrectVAT = recalculated - originalUnitPrice;
                }
                this.unitPrice = recalculated;
            }
        }
    }
    /**
     * Important, this influences VAT calculatio. Items are grouped by key and VAT is calculated per group.
     */
    static getVATUniqueKey(item) {
        if (item.VATExcempt === null) {
            return `vat_${item.VATPercentage}`;
        }
        return `excempt_${item.VATExcempt}_${item.VATExcempt}`;
    }
    static groupPerVATCategory(items) {
        // For every VAT category, calculate the taxable amount and VAT amount
        const categories = new Map();
        for (const item of items) {
            const key = this.getVATUniqueKey(item);
            let category = categories.get(key);
            if (!category) {
                category = [];
                categories.set(key, category);
            }
            category.push(item);
        }
        return [...categories.values()];
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], InvoicedBalanceItem.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], InvoicedBalanceItem.prototype, "balanceItemId", void 0);
__decorate([
    field({ decoder: new EnumDecoder(BalanceItemType), version: 398 })
], InvoicedBalanceItem.prototype, "type", void 0);
__decorate([
    field({ decoder: StringDecoder })
], InvoicedBalanceItem.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], InvoicedBalanceItem.prototype, "description", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], InvoicedBalanceItem.prototype, "balanceInvoicedAmount", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], InvoicedBalanceItem.prototype, "quantity", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], InvoicedBalanceItem.prototype, "unitPrice", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], InvoicedBalanceItem.prototype, "VATPercentage", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], InvoicedBalanceItem.prototype, "VATIncluded", void 0);
__decorate([
    field({ decoder: new EnumDecoder(VATExcemptReason), nullable: true })
], InvoicedBalanceItem.prototype, "VATExcempt", void 0);
//# sourceMappingURL=InvoicedBalanceItem.js.map